import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../../../context/AuthProvider";
import logger from "../../../utils/logger";

interface VideoPageState {
  videoUrl?: string | null;
  user?: any;
  type?: string;
}

const ArchivedVideoPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { videoUrl, user, type } = (location.state as VideoPageState) || {};
  const activeUser = user || authUser;
  const title = type || "Archived Video";

  const handleFullscreenToggle = async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (err) {
      // swallow; fullscreen may be blocked by browser
      logger.error("Fullscreen error", err);
    }
  };

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const handleBack = () => navigate(-1);

  return (
    <div
      className={`min-h-screen ${
        isFullscreen
          ? "bg-black p-0"
          : "bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 p-4 md:p-8"
      }`}
    >
      <style>
        {`
          @keyframes watermark-drift {
            0% { transform: translate(4%, 6%); }
            20% { transform: translate(78%, 10%); }
            40% { transform: translate(55%, 82%); }
            60% { transform: translate(8%, 74%); }
            80% { transform: translate(82%, 52%); }
            100% { transform: translate(18%, 24%); }
          }
        `}
      </style>
      <div className={`${isFullscreen ? "" : "max-w-6xl mx-auto space-y-4"}`}>
        {!isFullscreen && (
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-xl font-semibold text-slate-800">{title}</h1>
            <div className="h-10 w-10" />
          </div>
        )}

        <div
          ref={containerRef}
          className={`relative overflow-hidden ${
            isFullscreen
              ? "bg-black h-screen w-full"
              : "bg-slate-900 rounded-3xl shadow-2xl border border-slate-800 p-4 md:p-6"
          }`}
        >
          {!isFullscreen && (
            <div className="flex items-center justify-between mb-4 text-sm text-slate-200">
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 shadow-inner">
                Windowed mode
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleFullscreenToggle}
                  className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 text-white hover:bg-white/15 shadow-sm transition"
                >
                  Fullscreen
                </button>
              </div>
            </div>
          )}

          {isFullscreen && (
            <div className="absolute top-3 right-3 z-30 flex items-center gap-2 text-xs text-white/80">
              <span className="hidden sm:inline px-2 py-1 rounded bg-white/10 border border-white/15">Fullscreen</span>
              <button
                onClick={handleFullscreenToggle}
                className="px-3 py-1.5 rounded-lg bg-white/15 border border-white/20 text-white hover:bg-white/20 shadow-sm transition"
              >
                Exit
              </button>
            </div>
          )}

          <div
            className={`relative bg-black overflow-hidden ${
              isFullscreen ? "h-full" : "rounded-2xl border border-slate-800 shadow-inner"
            }`}
          >
            {activeUser && (
              <div
                className="pointer-events-none absolute z-20 px-3 py-2 sm:px-4 sm:py-3 rounded-lg bg-white/8 text-white/80 text-[11px] sm:text-xs font-semibold border border-white/10 shadow-xl"
                style={{
                  top: 0,
                  left: 0,
                  animation: "watermark-drift 26s linear infinite alternate",
                  backdropFilter: "blur(10px)",
                }}
              >
                <div className="leading-tight">Name: {activeUser.name || `${activeUser.firstName || ""} ${activeUser.lastName || ""}`.trim()}</div>
                <div className="leading-tight">Email: {activeUser.email}</div>
                <div className="leading-tight">IP: 127.0.0.1</div>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/35 pointer-events-none z-10" />

            {videoUrl ? (
              <div
                className={`relative ${
                  isFullscreen
                    ? "h-screen"
                    : "aspect-video w-full max-h-[82vh]"
                }`}
              >
                <video
                  controls
                  controlsList="nodownload nofullscreen"
                  className="absolute inset-0 w-full h-full bg-black object-contain"
                >
                  <source src={videoUrl} type="video/mp4" />
                </video>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[50vh] text-slate-300">
                <p className="text-lg font-medium mb-2">No video to play</p>
                <p className="text-sm text-slate-400">Try launching the video again from Archival Requests.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchivedVideoPage;

